/**
 * Shared uniqueness helpers for Custom List alternate import values (HA-2496).
 *
 * MUST behave byte-identically with the TS counterpart at
 * `athena/amplify/custom-lists/lib/alternateImportValues.ts`.
 *
 * Normalization rule:
 *   trim() then toLowerCase(). null / undefined / whitespace-only -> ''
 */

/**
 * @typedef {Object} OptionInput
 * @property {string|null} [id]
 * @property {string|null|undefined} option
 * @property {Array<string|null|undefined>|null} [alternateImportValues]
 * @property {boolean|null} [isEnabled]
 */

/**
 * @typedef {'label-vs-label'|'label-vs-synonym'|'synonym-vs-synonym'|'self-synonym-matches-own-label'} ConflictKind
 */

/**
 * @typedef {Object} ConflictOccurrence
 * @property {string|null} [optionId]
 * @property {string} rawValue
 * @property {'label'|'synonym'} source
 */

/**
 * @typedef {Object} UniquenessConflict
 * @property {string} normalizedValue
 * @property {ConflictKind} kind
 * @property {ConflictOccurrence[]} occurrences
 */

/**
 * Normalize a candidate value for comparison. null/undefined/whitespace -> ''.
 * @param {string|null|undefined} value
 * @returns {string}
 */
export function normalizeValue(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim().toLowerCase()
}

/**
 * @param {ReadonlyArray<OptionInput>} options
 * @param {boolean} allowAlternateImportValues
 * @returns {Map<string, ConflictOccurrence[]>}
 */
function indexOptions(options, allowAlternateImportValues) {
  const index = new Map()
  for (const option of options) {
    if (option && option.isEnabled === false) continue

    const rawLabel = option && option.option != null ? option.option : ''
    const normalizedLabel = normalizeValue(rawLabel)
    if (normalizedLabel !== '') {
      const bucket = index.get(normalizedLabel) || []
      bucket.push({
        optionId: option ? option.id : undefined,
        rawValue: String(rawLabel),
        source: 'label'
      })
      index.set(normalizedLabel, bucket)
    }

    if (!allowAlternateImportValues) continue

    const synonyms = option && Array.isArray(option.alternateImportValues)
      ? option.alternateImportValues
      : []
    for (const synonym of synonyms) {
      const normalized = normalizeValue(synonym)
      if (normalized === '') continue
      const bucket = index.get(normalized) || []
      bucket.push({
        optionId: option ? option.id : undefined,
        rawValue: String(synonym == null ? '' : synonym),
        source: 'synonym'
      })
      index.set(normalized, bucket)
    }
  }
  return index
}

/**
 * @param {ReadonlyArray<ConflictOccurrence>} occurrences
 * @returns {ConflictKind}
 */
function classifyConflict(occurrences) {
  const labels = occurrences.filter((o) => o.source === 'label')
  const synonyms = occurrences.filter((o) => o.source === 'synonym')

  if (labels.length >= 2) return 'label-vs-label'
  if (synonyms.length >= 2 && labels.length === 0) return 'synonym-vs-synonym'
  if (labels.length === 1 && synonyms.length >= 1) {
    const labelOwner = labels[0].optionId
    const selfMatch = synonyms.some((s) => s.optionId && s.optionId === labelOwner)
    return selfMatch ? 'self-synonym-matches-own-label' : 'label-vs-synonym'
  }
  return 'synonym-vs-synonym'
}

/**
 * Find collisions introduced (or preserved) by the transition from
 * `beforeOptions` to `afterOptions`. Grandfathered collisions are excluded.
 *
 * @param {ReadonlyArray<OptionInput>} beforeOptions
 * @param {ReadonlyArray<OptionInput>} afterOptions
 * @param {boolean} allowAlternateImportValues
 * @returns {UniquenessConflict[]}
 */
export function findIntroducedConflicts(beforeOptions, afterOptions, allowAlternateImportValues) {
  const afterIndex = indexOptions(afterOptions, allowAlternateImportValues)
  const beforeIndex = indexOptions(beforeOptions, allowAlternateImportValues)

  /** @type {UniquenessConflict[]} */
  const conflicts = []

  for (const [normalizedValue, occurrences] of afterIndex.entries()) {
    if (occurrences.length < 2) continue

    const afterOwners = new Set(
      occurrences.map((o) => `${o.optionId || ''}:${o.source}`)
    )
    const beforeOwners = new Set(
      (beforeIndex.get(normalizedValue) || []).map(
        (o) => `${o.optionId || ''}:${o.source}`
      )
    )

    let grandfathered = afterOwners.size === beforeOwners.size
    if (grandfathered) {
      for (const key of afterOwners) {
        if (!beforeOwners.has(key)) {
          grandfathered = false
          break
        }
      }
    }
    if (grandfathered) continue

    conflicts.push({
      normalizedValue,
      kind: classifyConflict(occurrences),
      occurrences: occurrences.map((o) => ({
        optionId: o.optionId,
        rawValue: o.rawValue,
        source: o.source
      }))
    })
  }

  return conflicts
}

/**
 * Enumerate ALL grandfathered collisions present in the current state. Used by
 * the Settings UI for the amber warning badge.
 *
 * @param {ReadonlyArray<OptionInput>} options
 * @param {boolean} allowAlternateImportValues
 * @returns {UniquenessConflict[]}
 */
export function findGrandfatheredConflicts(options, allowAlternateImportValues) {
  if (!allowAlternateImportValues) return []
  const index = indexOptions(options, allowAlternateImportValues)
  /** @type {UniquenessConflict[]} */
  const conflicts = []
  for (const [normalizedValue, occurrences] of index.entries()) {
    if (occurrences.length < 2) continue
    conflicts.push({
      normalizedValue,
      kind: classifyConflict(occurrences),
      occurrences: occurrences.map((o) => ({
        optionId: o.optionId,
        rawValue: o.rawValue,
        source: o.source
      }))
    })
  }
  return conflicts
}
